import React, { useEffect, useState, memo, useRef } from "react";
import { useSelector } from "react-redux";
import { Prompt } from "react-router-dom";
import { useDictionary } from "hooks";
import { Loader } from "semantic-ui-react";
import Icon from "@cayuse/shared-react.components.icon";

import { formSelectors } from "store/selectors";
import { setFormSavingIndicator } from "store/actions/form";

import styles from "./styles.css";

const preventLeavePageMessage =
    "The form is saving. Are you sure you want to leave the page?";

export const FormSaving = memo(() => {
    const { savingIndicator, isFileUploading } = useSelector(
        formSelectors.entity
    );
    const dictionary = useDictionary("common.formSaving");
    const [isSaved, setIsSaved] = useState(false);
    const timer = useRef(null);

    useEffect(() => {
        if (savingIndicator) {
            setIsSaved(true);

            window.onbeforeunload = (event) => {
                const e = event || window.event;
                e.preventDefault();
                e.returnValue = "";

                return "";
            };
        } else if (!savingIndicator && isSaved) {
            window.onbeforeunload = null;

            timer.current = setTimeout(() => {
                setIsSaved(false);
                timer.current = null;
            }, 3000);
        }
    }, [isSaved, savingIndicator]);

    useEffect(() => {
        return () => {
            setFormSavingIndicator(false);
            if (timer.current) {
                clearTimeout(timer.current);
                timer.current = null;
            }
        };
    }, []);

    if (savingIndicator || isFileUploading) {
        return (
            <div className={styles.saving} aria-live="polite">
                <Prompt
                    when={true}
                    message={
                        dictionary.preventLeavePageMessage ||
                        preventLeavePageMessage
                    }
                />
                <p className={styles.loaderText}>
                    {savingIndicator
                        ? dictionary.savingText || "Saving..."
                        : dictionary.uploadingText || "Uploading..."}
                </p>
                <Loader active size="tiny" inline />
            </div>
        );
    }
    if (!savingIndicator && !isFileUploading && isSaved) {
        return (
            <div className={styles.saving} aria-live="polite">
                <div className={styles.saved}>
                    <Icon
                        className={styles.saveIcon}
                        data-for="react-tooltip"
                        data-tip={
                            dictionary.saveCompleteText || "Changes Saved"
                        }
                        name={"check"}
                        size="bigger"
                    />
                    <p>{dictionary.saveCompleteText || "Changes Saved"}</p>
                </div>
            </div>
        );
    }
    return (
        <div className={styles.saving} aria-live="polite">
            <p>{dictionary.defaultText || "All Changes Save Automatically"}</p>
        </div>
    );
});

export default FormSaving;
